import React from 'react';
import { motion } from 'framer-motion';

const SkillRadar = ({
    skills = {
        comunicacao: 80,
        tecnica: 70,
        proatividade: 75,
        trabalhoEquipe: 85,
        pontualidade: 90
    },
    readOnly = true,
    onChange
}) => {
    // Normalize keys to ensure we have 5 axes
    const data = [
        { key: 'comunicacao', label: 'Comunicação', value: skills.comunicacao || 50 },
        { key: 'tecnica', label: 'Técnica', value: skills.tecnica || 50 },
        { key: 'proatividade', label: 'Proatividade', value: skills.proatividade || 50 },
        { key: 'trabalhoEquipe', label: 'Equipe', value: skills.trabalhoEquipe || 50 },
        { key: 'pontualidade', label: 'Pontualidade', value: skills.pontualidade || 50 },
    ];

    const size = 300;
    const center = size / 2;
    const radius = size / 2 - 60; // relative padding for labels
    const levels = [0.2, 0.4, 0.6, 0.8, 1.0];

    // Helper to get coordinates
    const getPoint = (value, index, total) => {
        const angle = (Math.PI * 2 * index) / total - Math.PI / 2;
        const r = (value / 100) * radius;
        return {
            x: center + r * Math.cos(angle),
            y: center + r * Math.sin(angle)
        };
    };

    // Generate Polygon Points
    const polygonPoints = data.map((d, i) => {
        const p = getPoint(d.value, i, data.length);
        return `${p.x},${p.y}`;
    }).join(' ');

    return (
        <div className="flex flex-col items-center justify-center relative select-none p-4">
            <svg width={size} height={size} className="overflow-visible drop-shadow-[0_0_20px_rgba(0,0,0,0.5)]">
                <defs>
                    <radialGradient id="radarGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                        <stop offset="0%" stopColor="#a855f7" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.1" />
                    </radialGradient>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Background Grid (Pentagons) */}
                {levels.map((level, i) => (
                    <polygon
                        key={i}
                        points={data.map((_, idx) => {
                            const p = getPoint(level * 100, idx, data.length);
                            return `${p.x},${p.y}`;
                        }).join(' ')}
                        fill="none"
                        stroke={i === levels.length - 1 ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.03)"}
                        strokeWidth={i === levels.length - 1 ? "1.5" : "1"}
                    />
                ))}

                {/* Axes */}
                {data.map((_, i) => {
                    const p = getPoint(100, i, data.length);
                    return (
                        <line
                            key={i}
                            x1={center}
                            y1={center}
                            x2={p.x}
                            y2={p.y}
                            stroke="rgba(255,255,255,0.05)"
                            strokeWidth="1"
                            strokeDasharray="4,4"
                        />
                    );
                })}

                {/* Data Polygon */}
                <motion.polygon
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: 'spring', damping: 20, stiffness: 100 }}
                    points={polygonPoints}
                    fill="url(#radarGradient)"
                    stroke="#a855f7"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    filter="url(#glow)"
                />

                {/* Data Points and Labels */}
                {data.map((d, i) => {
                    const p = getPoint(d.value, i, data.length);
                    const labelPos = getPoint(125, i, data.length);
                    const valuePos = getPoint(d.value, i, data.length);

                    return (
                        <g key={i}>
                            {/* Connection Dots */}
                            <motion.circle
                                initial={{ r: 0 }}
                                animate={{ r: 4.5 }}
                                cx={p.x}
                                cy={p.y}
                                fill="#a855f7"
                                stroke="#fff"
                                strokeWidth="1.5"
                                className="drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]"
                            />

                            {/* Axis Labels */}
                            <text
                                x={labelPos.x}
                                y={labelPos.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="text-[10px] fill-zinc-400 font-black uppercase tracking-[0.15em] drop-shadow-sm"
                            >
                                {d.label}
                            </text>

                            {/* Value Bubble (Floating near point) */}
                            <g transform={`translate(${p.x}, ${p.y - 15})`}>
                                <rect
                                    x="-12"
                                    y="-8"
                                    width="24"
                                    height="16"
                                    rx="4"
                                    fill="rgba(0,0,0,0.6)"
                                    className="backdrop-blur-md stroke-white/10"
                                />
                                <text
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                    className="text-[9px] fill-white font-black"
                                >
                                    {d.value}
                                </text>
                            </g>
                        </g>
                    );
                })}
            </svg>

            {!readOnly && (
                <div className="grid grid-cols-2 gap-3 mt-6 w-full max-w-xs p-4 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/5 shadow-2xl">
                    {data.map((d) => (
                        <div key={d.key} className="flex flex-col gap-1">
                            <label className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">{d.label}</label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={d.value}
                                onChange={(e) => onChange(d.key, parseInt(e.target.value) || 0)}
                                className="bg-black/40 border border-white/10 rounded-lg text-white text-xs py-1.5 px-2 focus:border-purple-500/50 outline-none transition-all"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SkillRadar;
